'use client';

import * as React from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import type { TFunction } from 'i18next';
import { BarChart3, FolderOpen, Info, Layers, Pin, Tag } from 'lucide-react';
import { Link } from 'react-router-dom';

import { DataTableCheckbox } from '@/components/data-table/data-table-checkbox';
import { DataTableColumnHeader } from '@/components/data-table/data-table-column-header';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import {
  REPORT_LIBRARY_ANALYSIS_FOCUSES,
  REPORT_LIBRARY_CATEGORIES,
} from '@/app/reports/constants';
import type { ReportLibraryRow } from '@/app/reports/types/report-library-row';
import { formatDate } from '@/lib/format';

type GetReportsTableColumnsProps = {
  t: TFunction<'reports'>;
  reportDetailsHref: (reportId: string) => string;
  onTogglePin: (reportId: string) => void;
};

const formatLastViewed = (iso: string | null) => {
  if (!iso) return '—';
  return formatDate(new Date(iso), 'MMM d, yyyy');
};

const buildCategoryOptions = (t: TFunction<'reports'>) =>
  REPORT_LIBRARY_CATEGORIES.map((category) => ({
    label: t(`categories.${category}`),
    value: category,
  }));

const buildAnalysisFocusOptions = (t: TFunction<'reports'>) =>
  REPORT_LIBRARY_ANALYSIS_FOCUSES.map((focus) => ({
    label: t(`analysisFocus.${focus}`),
    value: focus,
  }));

export const getReportsTableColumns = ({
  t,
  reportDetailsHref,
  onTogglePin,
}: GetReportsTableColumnsProps): ColumnDef<ReportLibraryRow>[] => [
  {
    id: 'select',
    header: ({ table }) => (
      <Checkbox
        checked={table.getIsAllPageRowsSelected() || (table.getIsSomePageRowsSelected() && 'indeterminate')}
        onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
        aria-label={t('table.selectAll')}
        className="translate-y-0.5"
      />
    ),
    cell: ({ row }) => <DataTableCheckbox row={row} />,
    enableSorting: false,
    enableHiding: false,
    size: 40,
  },
  {
    id: 'title',
    accessorKey: 'title',
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('library.columns.name')} />,
    cell: ({ row }) => {
      const report = row.original;
      return (
        <div className="flex min-w-0 items-center gap-2">
          <Link
            to={reportDetailsHref(report.id)}
            className="truncate text-sm font-medium text-foreground hover:underline"
            onClick={(event) => event.stopPropagation()}
          >
            {report.title}
          </Link>
          <Tooltip>
            <TooltipTrigger asChild>
              <Info className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">{report.description}</TooltipContent>
          </Tooltip>
        </div>
      );
    },
    enableSorting: true,
    meta: { label: t('library.columns.name') },
    size: 280,
  },
  {
    id: 'category',
    accessorKey: 'category',
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('library.columns.category')} />,
    cell: ({ row }) => (
      <Badge variant="secondary">{t(`categories.${row.original.category}`)}</Badge>
    ),
    enableSorting: true,
    enableColumnFilter: true,
    meta: {
      label: t('library.columns.category'),
      variant: 'multiSelect',
      options: buildCategoryOptions(t),
      icon: () => <Tag />,
    },
    size: 140,
  },
  {
    id: 'analysisFocus',
    accessorKey: 'analysisFocus',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title={t('library.columns.analysisFocus')} />
    ),
    cell: ({ row }) => (
      <Badge variant="outline">{t(`analysisFocus.${row.original.analysisFocus}`)}</Badge>
    ),
    enableSorting: true,
    enableColumnFilter: true,
    meta: {
      label: t('library.columns.analysisFocus'),
      variant: 'multiSelect',
      options: buildAnalysisFocusOptions(t),
      icon: () => <BarChart3 />,
    },
    size: 150,
  },
  {
    id: 'visualizationCount',
    accessorKey: 'visualizationCount',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title={t('library.columns.visualizations')} />
    ),
    cell: ({ row }) => (
      <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
        <Layers className="h-3.5 w-3.5" />
        {row.original.visualizationCount}
      </div>
    ),
    enableSorting: true,
    meta: { label: t('library.columns.visualizations') },
    size: 110,
  },
  {
    id: 'lastViewedAt',
    accessorKey: 'lastViewedAt',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title={t('library.columns.lastViewed')} />
    ),
    cell: ({ row }) => (
      <span className="text-sm text-muted-foreground">
        {formatLastViewed(row.original.lastViewedAt)}
      </span>
    ),
    enableSorting: true,
    sortingFn: (rowA, rowB) =>
      (rowA.original.lastViewedAt ?? '').localeCompare(rowB.original.lastViewedAt ?? ''),
    meta: { label: t('library.columns.lastViewed') },
    size: 140,
  },
  {
    id: 'createdBy',
    accessorKey: 'createdBy',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title={t('library.columns.createdBy')} />
    ),
    cell: ({ row }) => <span className="text-sm">{row.original.createdBy}</span>,
    enableSorting: true,
    meta: { label: t('library.columns.createdBy') },
    size: 120,
  },
  {
    id: 'isPinned',
    accessorKey: 'isPinned',
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('library.columns.pinned')} />,
    cell: ({ row }) => {
      const report = row.original;
      return (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          aria-label={t('library.pin')}
          onClick={(event) => {
            event.stopPropagation();
            onTogglePin(report.id);
          }}
        >
          <Pin
            className={`h-4 w-4 ${report.isPinned ? 'fill-current text-primary' : 'text-muted-foreground'}`}
          />
        </Button>
      );
    },
    enableSorting: true,
    enableColumnFilter: true,
    filterFn: (row, _columnId, value) => {
      if (!value || !Array.isArray(value) || value.length === 0) return true;
      const pinned = row.original.isPinned ? 'true' : 'false';
      return value.includes(pinned);
    },
    meta: {
      label: t('library.columns.pinned'),
      variant: 'multiSelect',
      options: [
        { label: t('table.filters.pinnedYes'), value: 'true' },
        { label: t('table.filters.pinnedNo'), value: 'false' },
      ],
      icon: () => <FolderOpen />,
    },
    size: 80,
  },
];
